/**
 * Visitor tracking API service
 * Handles capturing and storing comprehensive visitor data for every visit
 */

import { fetchApi } from '@/lib/utils/api';
import { API_ENDPOINTS } from './endpoints';

export interface VisitorData {
  // Basic visitor info
  visitorId: string;
  sessionId: string;
  timestamp: string;
  visitNumber: number;

  // User context (if logged in)
  userId?: string;
  userEmail?: string;
  userName?: string;
  userRole?: string;

  // Location data (from IP geolocation)
  ipAddress?: string;
  city?: string;
  region?: string;
  country?: string;
  countryCode?: string;
  callingCode?: string;
  timezone?: string;
  latitude?: number;
  longitude?: number;
  postalCode?: string;
  isp?: string;
  organization?: string;

  // Device & Browser info
  userAgent: string;
  deviceType: 'mobile' | 'desktop' | 'tablet';
  browser: string;
  browserVersion?: string;
  operatingSystem?: string;
  osVersion?: string;
  screenResolution?: string;
  viewportSize?: string;
  colorDepth?: number;
  pixelRatio?: number;
  touchSupport?: boolean;
  cookieEnabled?: boolean;
  javaEnabled?: boolean;

  // Network info
  connectionType?: string;
  connectionSpeed?: string;
  connectionDownlink?: number;
  connectionRtt?: number;
  onlineStatus?: boolean;

  // Page context
  pageUrl: string;
  pagePath: string;
  pageTitle?: string;
  referrer?: string;
  referrerDomain?: string;
  language: string;
  languages?: string[];

  // UTM & Campaign data
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;

  // Additional tracking IDs
  gaClientId?: string;
  gaSessionId?: string;
  fbClickId?: string;
  gclid?: string;

  // Performance metrics
  pageLoadTime?: number;
  domLoadTime?: number;
  firstPaintTime?: number;
  firstContentfulPaintTime?: number;

  // Session & Engagement data
  sessionStartTime: number;
  timeOnPage?: number;
  scrollDepth?: number;
  interactionCount?: number;

  // Technical details
  webglSupport?: boolean;
  webrtcSupport?: boolean;
  localStorageSupport?: boolean;
  sessionStorageSupport?: boolean;
  indexedDbSupport?: boolean;

  // Additional browser capabilities
  doNotTrack?: boolean;
  adBlocker?: boolean;
  plugins?: string[];

  // Visit metadata
  isReturningVisitor: boolean;
  daysSinceFirstVisit?: number;
  totalVisits?: number;
  lastVisitDate?: string | null;
}

export interface VisitorTrackingResponse {
  success: boolean;
  visitorId: string;
  visitId?: string;
  message?: string;
}

export const visitorService = {
  /**
   * Track visitor data for every visit
   */
  async trackVisitor(data: VisitorData): Promise<VisitorTrackingResponse> {
    const response = await fetchApi<VisitorTrackingResponse>(
      `${API_ENDPOINTS.VISITORS.TRACK}`,
      {
        method: 'POST',
        body: JSON.stringify(data),
        requireAuth: false, // Visitors may not be authenticated
      }
    );
    return response.result;
  },

  /**
   * Track single journey event to backend database
   */
  async trackJourneyEvent(
    visitorId: string,
    event: {
      event: string;
      page: string;
      timestamp: number;
      data?: Record<string, unknown>;
    }
  ): Promise<VisitorTrackingResponse> {
    const response = await fetchApi<VisitorTrackingResponse>(
      `${API_ENDPOINTS.VISITORS.JOURNEY}`,
      {
        method: 'POST',
        body: JSON.stringify({
          visitorId,
          ...event,
        }),
        requireAuth: false,
      }
    );
    return response.result;
  },

  /**
   * Track batch of journey events to backend database (more efficient)
   */
  async trackJourneyEventsBatch(
    visitorId: string,
    events: Array<{
      clientEventId?: string;
      event: string;
      page: string;
      timestamp: number;
      data?: Record<string, unknown>;
    }>
  ): Promise<VisitorTrackingResponse> {
    const response = await fetchApi<VisitorTrackingResponse>(
      `${API_ENDPOINTS.VISITORS.JOURNEY_BATCH}`,
      {
        method: 'POST',
        body: JSON.stringify({
          visitorId,
          events,
        }),
        requireAuth: false,
      }
    );
    return response.result;
  },

  /**
   * Update visitor session data (for ongoing session tracking)
   */
  async updateVisitorSession(
    visitorId: string,
    sessionData: {
      timeOnPage?: number;
      scrollDepth?: number;
      interactionCount?: number;
      engagementEvents?: Array<{
        type: string;
        timestamp: number;
        data?: Record<string, unknown>;
      }>;
      // Add user context fields for when user logs in
      userId?: string;
      userEmail?: string;
      userName?: string;
      userRole?: string;
      userContextSetAt?: number;
    }
  ): Promise<VisitorTrackingResponse> {
    const response = await fetchApi<VisitorTrackingResponse>(
      `${API_ENDPOINTS.VISITORS.SESSION(visitorId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(sessionData),
        requireAuth: false,
      }
    );
    return response.result;
  },
};
